import { fullLabel } from "../components/WhereItIs";
import { useEffect, useState, useMemo } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { notifyPdfNoStamp } from "@/lib/printNotice";
import { Printer, FileText, MapPin, Search, Layers, Copy, Building2 } from "lucide-react";
import { printZpl, printZplChunked, PrintAborted, ZEBRA_FLUSH_HINT, setSelectedDevice, clearSelectedDevice } from "@/lib/zebra";
import { usePrintJob } from "@/lib/usePrintJob";
import { usePrintConfirm } from "@/lib/usePrintConfirm";
import { usePrinterTarget } from "@/lib/usePrinterTarget";
import { printerDisplayName } from "@/lib/printerMap";
import { mediaToSize, mediaLabelOf } from "@/lib/tagMedia";
import { MediaPicker } from "@/components/MediaPicker";
import { PrintDestination } from "@/components/PrintDestination";
import { PrintStopBanner } from "@/components/PrintStopBanner";
import { PrintRemainderBanner } from "@/components/PrintRemainderBanner";
import { getPrintChunkSize } from "@/lib/printSettings";
import { ScanTextInput } from "@/components/ScanTextInput";

export default function LocationLabelsPage() {
  const [code, setCode] = useState("");
  const [locs, setLocs] = useState([]);
  const [locsError, setLocsError] = useState(false);
  const [q, setQ] = useState("");
  // SCALE-01: the picker is server-searched (no client slice hiding rows). Honest has_more.
  // NG-3: a failed search must never look like a legit zero-result — `error` is surfaced.
  const [picker, setPicker] = useState({ items: [], has_more: false, loading: true, error: false });
  const [copies, setCopies] = useState(2);
  const [printing, setPrinting] = useState(false);
  const printJob = usePrintJob();
  const [remainder, setRemainder] = useState(null);   // run-scoped: { title, media, labels: [{zpl,id}] }
  const { printConfirmDialog, confirmAndRun } = usePrintConfirm();
  const [media, setMedia] = useState("sticker");   // fixed-location labels: any stock
  const printer = usePrinterTarget(media);

  // Build a Site → Zone → Structure scope tree with spot counts (spots = printable positions)
  const scopeTree = useMemo(() => {
    const spotsUnder = (id) => locs.filter((l) => l.type === "spot" && (l.path_ids || []).includes(id)).length;
    const sites = locs.filter((l) => l.type === "site");
    return sites.map((site) => ({
      ...site, spots: spotsUnder(site.id),
      zones: locs.filter((z) => z.type === "zone" && z.parent_id === site.id).map((zone) => ({
        ...zone, spots: spotsUnder(zone.id),
        structures: locs.filter((st) => st.type === "structure" && st.parent_id === zone.id).map((st) => ({ ...st, spots: spotsUnder(st.id) })),
      })),
    }));
  }, [locs]);
  const totalSpots = useMemo(() => locs.filter((l) => l.type === "spot").length, [locs]);

  const printScope = (nodeId, name, spotCount) => {
    if (printing) return;
    if (spotCount === 0) { toast.error(`No spots to print under ${name}.`); return; }
    confirmAndRun({ title: `Print ${name}`, labelCount: spotCount, copies, noun: "labels", mediaType: media }, async () => {
      setPrinting(true);
      const tId = toast.loading(`Preparing labels for ${name}…`);
      try {
        const { data } = await api.get("/locations/label-batch-zpl", { params: { node_id: nodeId, size: mediaToSize(media) } });
        const list = [];
        const idByIndex = [];
        const locIds = (data.labels || []).map((l) => l.id).filter(Boolean);
        data.labels.forEach((l) => { for (let i = 0; i < copies; i++) { list.push(l.zpl); idByIndex.push(l.id); } });
        if (!list.length) { toast.error(`No labels to print for ${name}.`, { id: tId }); return; }
        const signal = printJob.start(`${name} · ${data.count} spots × ${copies}`, list.length);
        try {
          const dev = await printZplChunked(list, { chunkSize: getPrintChunkSize(), onProgress: (done, total) => {
            printJob.progress(done, total);
            toast.loading(`Printing ${done}/${total} labels for ${name}…`, { id: tId });
          }, signal, mediaType: media });
          // confirm-driven stamp: reached only if the run finished without abort/throw
          if (locIds.length) await api.post("/locations/label-printed/confirm", { location_ids: locIds }).catch(() => {});
          toast.success(`Sent ${list.length} labels for ${name} (${data.count} spots × ${copies}) to ${printerDisplayName(dev)}.`, { id: tId });
        } catch (e) {
          if (e instanceof PrintAborted || e.aborted) {
            // honest partial: spots whose label fully went out ARE printed — record exactly those
            const sentIds = [...new Set(idByIndex.slice(0, e.done ?? 0).filter(Boolean))];
            if (sentIds.length) await api.post("/locations/label-printed/confirm", { location_ids: sentIds }).catch(() => {});
            // run-scoped remainder: keep the exact unsent labels for a one-tap finish
            const rest = list.slice(e.done ?? 0).map((z, i) => ({ zpl: z, id: idByIndex[(e.done ?? 0) + i] }));
            if (rest.length) setRemainder({ title: name, media, labels: rest });
            toast.error(`Stopped after ${e.done ?? 0} of ${e.total ?? list.length} labels — ${sentIds.length} spot(s) that fully printed are recorded as printed. ${ZEBRA_FLUSH_HINT}`, { id: tId, duration: 12000 });
          }
          else toast.error(`Zebra Browser Print not reachable — use the PDF, or check the printer connection. (${e.message})`, { id: tId });
        } finally { printJob.end(); }
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail || e.message), { id: tId });
      } finally { setPrinting(false); }
    });
  };

  useEffect(() => {
    api.get("/locations").then(({ data }) => { setLocs(data || []); setLocsError(false); })
      .catch(() => setLocsError(true));
  }, []);

  const printZ = async (value) => {
    const v = (value || "").trim();
    if (!v) { toast.error("Enter or pick a location code."); return; }
    if (!printer.target) { toast.error(printer.needsSetup ? `No printer is set for ${mediaLabelOf(media)} stock. Open Printer Setup and map it — nothing was printed.` : "No printer detected — start Zebra Browser Print."); return; }
    setSelectedDevice(printer.target);
    try {
      const { data } = await api.get("/locations/label-zpl", { params: { value: v, size: mediaToSize(media) } });
      const dev = await printZpl(data.zpl, null, media);   // the SELECTED media's printer — mismatch is refused
      // confirm-driven stamp: only when it resolves to a real saved location (test codes have no id)
      if (data.id) await api.post("/locations/label-printed/confirm", { location_ids: [data.id] }).catch(() => {});
      toast.success(`Sent ${data.code} to ${printerDisplayName(dev)}${data.resolved ? "" : " — TEST code (not a saved location)"}.`);
    } catch (e) {
      const msg = String(e?.message || "").toLowerCase().includes("fetch")
        ? "Zebra Browser Print not reachable — use the PDF, or check the printer connection."
        : formatApiErrorDetail(e.response?.data?.detail || e.message);
      toast.error(msg);
    } finally { clearSelectedDevice(); }
  };

  // Print the exact unsent remainder of an interrupted bulk run (routes on the media the
  // run was BUILT with, not the current picker — layout and destination can't disagree).
  const printRemainderNow = () => {
    if (!remainder) return;
    const { title, media: rMedia, labels } = remainder;
    confirmAndRun({ title: `Print remainder · ${title}`, labelCount: labels.length, copies: 1, noun: "labels", mediaType: rMedia }, async () => {
      setRemainder(null);
      const tId = toast.loading(`Printing remaining ${labels.length} labels…`);
      const signal = printJob.start(`Remainder · ${title}`, labels.length);
      try {
        await printZplChunked(labels.map((l) => l.zpl), { chunkSize: getPrintChunkSize(), onProgress: (done, total) => {
          printJob.progress(done, total);
          toast.loading(`Printing ${done}/${total} labels…`, { id: tId });
        }, signal, mediaType: rMedia });
        const ids = [...new Set(labels.map((l) => l.id).filter(Boolean))];
        if (ids.length) await api.post("/locations/label-printed/confirm", { location_ids: ids }).catch(() => {});
        toast.success(`Printed the remaining ${labels.length} labels.`, { id: tId });
      } catch (e) {
        if (e instanceof PrintAborted || e.aborted) {
          const sentIds = [...new Set(labels.slice(0, e.done ?? 0).map((l) => l.id).filter(Boolean))];
          if (sentIds.length) await api.post("/locations/label-printed/confirm", { location_ids: sentIds }).catch(() => {});
          const rest = labels.slice(e.done ?? 0);
          if (rest.length) setRemainder({ title, media: rMedia, labels: rest });
          toast.error(`Stopped after ${e.done ?? 0} of ${labels.length} labels — the ones that fully printed are recorded. ${ZEBRA_FLUSH_HINT}`, { id: tId, duration: 12000 });
        } else toast.error(`Zebra Browser Print not reachable — check the printer connection. (${e.message})`, { id: tId });
      } finally { printJob.end(); }
    });
  };

  const openPdf = async (value) => {
    const v = (value || "").trim();
    if (!v) { toast.error("Enter or pick a location code."); return; }
    try {
      const res = await api.get("/locations/label-pdf", { params: { value: v, size: mediaToSize(media) }, responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
      notifyPdfNoStamp();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail || e.message)); }
  };

  // SCALE-01: server-searched picker — replaces the old client filter over the full list that
  // silently sliced to 200 rows. Debounced; asks the backend for matches (index-assisted).
  useEffect(() => {
    let alive = true;
    setPicker((p) => ({ ...p, loading: true }));
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/locations/labelable", { params: { q, limit: 100 } });
        if (alive) setPicker({ items: data.items || [], has_more: !!data.has_more, loading: false, error: false });
      } catch {
        if (alive) setPicker({ items: [], has_more: false, loading: false, error: true });
      }
    }, q ? 250 : 0);
    return () => { alive = false; clearTimeout(t); };
  }, [q]);

  return (
    <div data-testid="shelf-labels-page" className="max-w-3xl space-y-6">
      <div>
        <h1 className="font-display text-2xl font-black tracking-tight">Fixed Location Labels</h1>
        <p className="text-sm text-slate-500">{mediaLabelOf(media)} · 300 DPI · code-forward · QR + barcode · no branding. Media picker below switches stock.</p>
      </div>

      {/* Printer destination — follows the selected media (sticker / hang tag / 3x1) */}
      <PrintDestination {...printer} testid="shelf-destination" />

      <PrintRemainderBanner remainder={remainder} onPrint={printRemainderNow} onDismiss={() => setRemainder(null)} />

      {/* Bulk print by area */}
      <div data-testid="bulk-print-card" className="rounded-xl border border-slate-200 p-4 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-[#B01E28]" />
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Bulk print by area</Label>
          </div>
          <div className="flex items-center gap-2">
            <Copy className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-xs text-slate-500">Copies per label</span>
            <Select value={String(copies)} onValueChange={(v) => setCopies(Number(v))}>
              <SelectTrigger data-testid="copies-select" className="h-8 w-16 rounded-lg"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1" data-testid="copies-1">1</SelectItem>
                <SelectItem value="2" data-testid="copies-2">2</SelectItem>
                <SelectItem value="3" data-testid="copies-3">3</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <p className="text-[11px] text-slate-400">Each copy is identical (same code, same QR, same scan target) — set 2 for 20-ft bays so you can mount a label at both ends. Labels print one at a time, paced for the Zebra.</p>

        <Button data-testid="print-all-spots" onClick={() => printScope("all", "ALL spots", totalSpots)} disabled={printing || totalSpots === 0}
          className="rounded-lg bg-[#B01E28] hover:bg-[#8F1820] text-white">
          <Printer className="w-4 h-4 mr-1.5" />Print ALL spots ({totalSpots} × {copies} = {totalSpots * copies} labels)
        </Button>

        <div className="divide-y divide-slate-100 max-h-[24rem] overflow-y-auto">
          {scopeTree.map((site) => (
            <div key={site.id} className="py-1">
              <div className="flex items-center gap-2 py-1.5">
                <Building2 className="w-4 h-4 text-[#B01E28]" />
                <span className="font-bold text-sm">{site.name}</span>
                <span className="font-mono text-[10px] text-slate-400">{site.short_code}</span>
                <Button data-testid={`print-scope-${site.short_code}`} size="sm" variant="outline" disabled={printing || site.spots === 0}
                  onClick={() => printScope(site.id, site.name, site.spots)} className="ml-auto rounded-lg h-7 border-slate-200">
                  <Printer className="w-3.5 h-3.5 mr-1" />{site.spots} spots
                </Button>
              </div>
              {site.zones.map((zone) => (
                <div key={zone.id} className="ml-4">
                  <div className="flex items-center gap-2 py-1.5">
                    <span className="text-sm text-slate-700">{zone.name}</span>
                    <span className="font-mono text-[10px] text-slate-400">{zone.short_code}</span>
                    <Button data-testid={`print-scope-${zone.short_code}`} size="sm" variant="outline" disabled={printing || zone.spots === 0}
                      onClick={() => printScope(zone.id, zone.name, zone.spots)} className="ml-auto rounded-lg h-7 border-slate-200">
                      <Printer className="w-3.5 h-3.5 mr-1" />{zone.spots} spots
                    </Button>
                  </div>
                  <div className="ml-4 grid grid-cols-1 sm:grid-cols-2 gap-1 pb-2">
                    {zone.structures.map((st) => (
                      <div key={st.id} className="flex items-center gap-2 py-1 px-2 rounded-lg hover:bg-slate-50">
                        <span className="text-sm text-slate-700 truncate">{st.name}</span>
                        <span className="font-mono text-[10px] text-slate-400 truncate">{st.short_code}</span>
                        <Button data-testid={`print-scope-${st.short_code}`} size="sm" disabled={printing || st.spots === 0}
                          onClick={() => printScope(st.id, st.name, st.spots)} className="ml-auto rounded-lg h-7 bg-[#1F1F1F] text-white shrink-0">
                          <Printer className="w-3.5 h-3.5 mr-1" />{st.spots}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ))}
          {scopeTree.length === 0 && (locsError
            ? <div data-testid="shelf-tree-error" className="text-sm text-[#B01E28] py-6 text-center font-mono">Couldn't load locations — network or server error. Reload to retry.</div>
            : <div className="text-sm text-slate-400 py-6 text-center font-mono">No saved locations yet — import them first.</div>)}
        </div>
      </div>

      {/* Type a code to test */}
      <div className="rounded-xl border border-slate-200 p-4 space-y-3">
        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Type a code to test</Label>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-slate-500">Label stock</span>
          <MediaPicker value={media} onChange={setMedia} testidPrefix="shelf-media" />
        </div>
        <div className="flex flex-wrap gap-2">
          <ScanTextInput testid="shelf-code-input" value={code} onChange={setCode}
            onEnter={() => { if (code.trim()) printZ(code); }}
            placeholder="e.g. ROY-WHSE-R1-B2-C" inputClassName="h-11" className="flex-1 min-w-[220px]" autoComplete="off" />
          <Button data-testid="shelf-print-zpl" onClick={() => printZ(code)} className="rounded-lg h-11 bg-[#1F1F1F] text-white"><Printer className="w-4 h-4 mr-1.5" />Print (ZPL)</Button>
          <Button data-testid="shelf-pdf" onClick={() => openPdf(code)} variant="outline" className="rounded-lg h-11"><FileText className="w-4 h-4 mr-1.5" />PDF</Button>
        </div>
        <p className="text-[11px] text-slate-400 font-mono">Any code prints for a stock test. Real codes pull their saved QR token automatically.</p>
      </div>

      {/* Pull from the saved set */}
      <div className="rounded-xl border border-slate-200 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Pull from saved locations</Label>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-slate-400" />
            <Input data-testid="shelf-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="search…" className="h-8 pl-7 text-sm w-48" />
          </div>
        </div>
        <div className="divide-y divide-slate-100 max-h-[28rem] overflow-y-auto">
          {picker.loading && <div className="text-sm text-slate-400 py-6 text-center font-mono">Searching…</div>}
          {!picker.loading && picker.error && <div data-testid="shelf-picker-error" className="text-sm text-[#B01E28] py-6 text-center font-mono">Search failed — network or server error, not zero matches. Try again.</div>}
          {!picker.loading && !picker.error && picker.items.length === 0 && <div className="text-sm text-slate-400 py-6 text-center font-mono">No locations match.</div>}
          {!picker.loading && picker.items.map((l) => (
            <div key={l.id} data-testid={`shelf-row-${l.short_code}`} className="flex items-center gap-3 py-2">
              <MapPin className="w-4 h-4 text-[#B01E28] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-mono text-sm font-bold truncate">{l.short_code}</div>
                <div className="text-[11px] text-slate-400 break-words">{fullLabel(l) || l.name}</div>
              </div>
              <Button data-testid={`shelf-zpl-${l.short_code}`} size="sm" onClick={() => printZ(l.short_code)} className="rounded-lg h-8 bg-[#1F1F1F] text-white"><Printer className="w-3.5 h-3.5 mr-1" />ZPL</Button>
              <Button data-testid={`shelf-pdf-${l.short_code}`} size="sm" variant="outline" onClick={() => openPdf(l.short_code)} className="rounded-lg h-8"><FileText className="w-3.5 h-3.5" /></Button>
            </div>
          ))}
          {!picker.loading && picker.has_more && (
            <div data-testid="shelf-picker-more" className="text-[11px] text-slate-500 py-3 text-center font-mono">
              Showing first {picker.items.length} matches — type more of the code to narrow.
            </div>
          )}
        </div>
      </div>

      {printConfirmDialog}
      <PrintStopBanner job={printJob.job} onStop={printJob.stop} />
    </div>
  );
}
